import { Link } from 'react-router-dom'
import { ArrowLeft, Image, Video, Music, MessageCircle, Volume2 } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/AIModelsPage.stylex'
import { useState } from 'react'

interface ModelCardProps {
    title: string
    description: string
    href: string
    highlights: string[]
    creditCost: string
    badge?: string
}

function ModelCard({ title, description, href, highlights, creditCost, badge }: ModelCardProps) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Link
            to={href}
            {...stylex.props(styles.modelCard, isHovered && styles.modelCardHoverActive)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(styles.modelCardHeader)}>
                <h3 {...stylex.props(styles.modelTitle)}>{title}</h3>
                {badge && <div {...stylex.props(styles.modelBadge)}>{badge}</div>}
            </div>
            <div {...stylex.props(styles.modelCardContent)}>
                <p {...stylex.props(styles.modelDescription)}>{description}</p>
                <ul {...stylex.props(styles.modelHighlights)}>
                    {highlights.map((highlight, index) => (
                        <li key={index} {...stylex.props(styles.highlightItem)}>{highlight}</li>
                    ))}
                </ul>
            </div>
            <div {...stylex.props(styles.modelCardFooter)}>
                <div {...stylex.props(styles.creditCost)}>{creditCost}</div>
                <div {...stylex.props(styles.learnMore, isHovered && styles.learnMoreHover)}>Learn More →</div>
            </div>
        </Link>
    )
}

export default function AIModelsPage() {
    const imageModels = [
        { title: "Google Imagen", description: "Best for creative expression with exceptional artistic capabilities and style versatility.", href: "/images-models/google-imagen", highlights: ["Text to Image", "Image to Image", "Best Editing Experience"], creditCost: "4 credits", badge: "Premium" },
        { title: "Runway Images", description: "Best for consistent characters with state-of-the-art image generation capabilities.", href: "/images-models/runway-image", highlights: ["Text to Image", "Image to Image", "Consistent Characters", "Multi-Image Reference"], creditCost: "8 credits" },
        { title: "Flux Pro", description: "Experience cutting-edge AI image generation with unmatched speed, quality, and prompt adherence.", href: "/images-models/flux-pro", highlights: ["Text to Image", "Image to Image", "High Quality"], creditCost: "6 credits" },
        { title: "Wan Images", description: "Best for realistic photography. Generate super realistic images from text descriptions.", href: "/images-models/wan-image", highlights: ["Text to Image", "Image to Image", "Photorealistic"], creditCost: "2.5 credits" },
        { title: "Qwen Images", description: "Best for fast prototyping, our most affordable model with excellent text rendering capabilities.", href: "/images-models/qwen-image", highlights: ["Text to Image", "Image to Image", "Text Rendering"], creditCost: "2 credits", badge: "Value" },
        { title: "See Dream V4", description: "Advanced AI model for 4K image generation. Create realistic visuals and precise multi-reference edits instantly, now available in AiBrush.", href: "/images-models/see-dream4", highlights: ["Text to Image", "Image to Image", "High Quality", "Multi-Image Reference"], creditCost: "3 credits", badge: "NEW" }
    ]

    const videoModels = [
        { title: "Google Veo", description: "State-of-the-art video generation with cinematic quality and precise control over visual elements.", href: "/videos-models/google-veo", highlights: ["Text to Video", "Image to Video", "Professional Grade"], creditCost: "100 credits", badge: "Premium" },
        { title: "Runway Video", description: "Professional video generation with advanced motion control and high-fidelity output.", href: "/videos-models/runway-video", highlights: ["Text to Video", "Image to Video", "Motion Control"], creditCost: "25 credits" },
        { title: "Pika Video", description: "Versatile video generation with excellent motion quality and creative flexibility.", href: "/videos-models/pika-video", highlights: ["Text to Video", "Image to Video", "Cost Effective"], creditCost: "20 credits", badge: "Value" },
        { title: "Hailuo Video", description: "High-quality video generation with realistic motion and excellent temporal consistency.", href: "/videos-models/hailuo-video", highlights: ["Text to Video", "Image to Video", "Cinematic shot replication"], creditCost: "50 credits" },
        { title: "Wan Video", description: "Efficient video generation with good quality-to-cost ratio for various use cases.", href: "/videos-models/wan-video", highlights: ["Text to Video", "Image to Video", "High Quality"], creditCost: "30 credits" },
        { title: "Pixverse Lipsync", description: "Advanced lip-sync technology for audio-driven video animation with precise mouth movements.", href: "/videos-models/pixverse-lipsync", highlights: ["Audio to Video", "Lip Synchronization"], creditCost: "25 credits" },
        { title: "Veed Lipsync", description: "Professional lip-sync solution for creating talking avatars and animated characters.", href: "/videos-models/veed-lipsync", highlights: ["Audio to Video", "Lip Synchronization"], creditCost: "40 credits" }
    ]

    const musicModels = [
        { title: "Suno Music", description: "Revolutionary text-to-music generation with multiple genres and vocal options.", href: "/music-models/suno", highlights: ["Text to Music", "Text to Song"], creditCost: "6 credits", badge: "Premium" },
        { title: "Google Lyria", description: "High-fidelity music generation with granular creative control and real-time capabilities.", href: "/music-models/google-lyria", highlights: ["Text to Music"], creditCost: "6 credits" }
    ]

    const speechModels = [
        { title: "Gemini Speech", description: "Advanced text-to-speech powered by Google's latest AI technology.", href: "/speech-models/gemini-speech", highlights: ["30 Voices", "Multi-Voice Conversations", "24 Languages"], creditCost: "0.3 credits", badge: "Premium" },
        { title: "OpenAI Speech", description: "Natural-sounding text-to-speech with multiple voices and language support.", href: "/speech-models/openai-speech", highlights: ["11 Voice Options", "110 Languages"], creditCost: "0.1 credits" },
    ]

    const soundEffectModels = [
        { title: "ElevenLabs Sound Effects", description: "Create cinematic soundscapes and immersive audio environments from text descriptions.", href: "/sound-effects-models/elevenlabs-sound-effects", highlights: ["Cinematic Quality", "Various Categories"], creditCost: "1 credit" }
    ]

    return (
        <div {...stylex.props(styles.aiModelsPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Image Models Section */}
            <section id="Image-Models" {...stylex.props(styles.categorySection)}>
                <div {...stylex.props(styles.categoryHeader)}>
                    <div {...stylex.props(styles.modelCardIcon)}>
                        <Image size={24} />
                    </div>
                    <div>
                        <h3 {...stylex.props(styles.categoryTitle)}>Image Models</h3>
                    </div>
                </div>
                <div {...stylex.props(styles.modelsGrid)}>
                    {imageModels.map((model, index) => (
                        <ModelCard key={index} {...model} />
                    ))}
                </div>
            </section>

            {/* Video Models Section */}
            <section id="Video-Models" {...stylex.props(styles.categorySection)}>
                <div {...stylex.props(styles.categoryHeader)}>
                    <div {...stylex.props(styles.modelCardIcon)}>
                        <Video size={24} />
                    </div>
                    <div>
                        <h3 {...stylex.props(styles.categoryTitle)}>Video Models</h3>
                    </div>
                </div>
                <div {...stylex.props(styles.modelsGrid)}>
                    {videoModels.map((model, index) => (
                        <ModelCard key={index} {...model} />
                    ))}
                </div>
            </section>

            {/* Music Models Section */}
            <section id="Music-Models" {...stylex.props(styles.categorySection)}>
                <div {...stylex.props(styles.categoryHeader)}>
                    <div {...stylex.props(styles.modelCardIcon)}>
                        <Music size={24} />
                    </div>
                    <div>
                        <h3 {...stylex.props(styles.categoryTitle)}>Music Models</h3>
                    </div>
                </div>
                <div {...stylex.props(styles.modelsGrid)}>
                    {musicModels.map((model, index) => (
                        <ModelCard key={index} {...model} />
                    ))}
                </div>
            </section>

            {/* Speech Models Section */}
            <section id="Speech-Models" {...stylex.props(styles.categorySection)}>
                <div {...stylex.props(styles.categoryHeader)}>
                    <div {...stylex.props(styles.modelCardIcon)}>
                        <MessageCircle size={24} />
                    </div>
                    <div>
                        <h3 {...stylex.props(styles.categoryTitle)}>Speech Models</h3>
                    </div>
                </div>
                <div {...stylex.props(styles.modelsGrid)}>
                    {speechModels.map((model, index) => (
                        <ModelCard key={index} {...model} />
                    ))}
                </div>
            </section>

            {/* Sound Effects Models Section */}
            <section id="Sound-Effects-Models" {...stylex.props(styles.categorySection)}>
                <div {...stylex.props(styles.categoryHeader)}>
                    <div {...stylex.props(styles.modelCardIcon)}>
                        <Volume2 size={24} />
                    </div>
                    <div>
                        <h3 {...stylex.props(styles.categoryTitle)}>Sound Effects Models</h3>
                    </div>
                </div>
                <div {...stylex.props(styles.modelsGrid)}>
                    {soundEffectModels.map((model, index) => (
                        <ModelCard key={index} {...model} />
                    ))}
                </div>
            </section>
        </div>
    )
}